/**
 * Popover — Base UI Popover primitive.
 *
 * Design system mapping:
 *   • Popup: bg-white dark:bg-webex-gray-950
 *            border webex-gray-300 dark:border-webex-gray-700
 *            rounded-lg shadow-overlay px-4 py-3
 *
 * Composes as compound parts so consumers can mix layouts:
 *   <Popover.Root>
 *     <Popover.Trigger render={<Button>Help</Button>} />
 *     <Popover.Content>...</Popover.Content>   // includes Portal+Positioner+Popup
 *   </Popover.Root>
 */
import * as React from "react"
import { Popover as BasePopover } from "@base-ui-components/react/popover"
import { cn } from "@lib/cn"

const Root = BasePopover.Root
const Trigger = BasePopover.Trigger
const Title = BasePopover.Title
const Description = BasePopover.Description
const Close = BasePopover.Close
const Arrow = BasePopover.Arrow

interface ContentProps extends React.ComponentPropsWithoutRef<typeof BasePopover.Popup> {
  side?: "top" | "right" | "bottom" | "left"
  align?: "start" | "center" | "end"
  sideOffset?: number
  withArrow?: boolean
}

const Content = React.forwardRef<HTMLDivElement, ContentProps>(
  ({ className, children, side = "bottom", align = "center", sideOffset = 6, withArrow = false, ...props }, ref) => (
    <BasePopover.Portal>
      <BasePopover.Positioner side={side} align={align} sideOffset={sideOffset} className="z-40">
        <BasePopover.Popup
          ref={ref}
          className={cn(
            "rounded-md border bg-white dark:bg-webex-gray-950",
            "border-webex-gray-300 dark:border-webex-gray-700",
            "shadow-[0_4px_12px_rgba(0,0,0,0.12)] dark:shadow-[0_4px_12px_rgba(0,0,0,0.6)]",
            "px-4 py-3 text-sm text-webex-gray-1000 dark:text-white",
            "outline-none",
            "data-[starting-style]:opacity-0 data-[ending-style]:opacity-0",
            "transition-opacity duration-150 motion-reduce:transition-none",
            className,
          )}
          {...props}
        >
          {withArrow && (
            <BasePopover.Arrow className="text-white dark:text-webex-gray-950">
              <ArrowSvg />
            </BasePopover.Arrow>
          )}
          {children}
        </BasePopover.Popup>
      </BasePopover.Positioner>
    </BasePopover.Portal>
  ),
)
Content.displayName = "Popover.Content"

function ArrowSvg() {
  return (
    <svg width="20" height="10" viewBox="0 0 20 10" fill="none" aria-hidden>
      <path d="M0 0L10 8L20 0Z" fill="currentColor" />
    </svg>
  )
}

export const Popover = {
  Root,
  Trigger,
  Content,
  Title,
  Description,
  Close,
  Arrow,
}
